import { useRef, forwardRef, RefObject } from "react";
import { useStyles } from "./Button.style";
import clsx from "classnames";
import { CircularProgress } from "../CircularProgress/CircularProgress";

type ButtonVariant = "contained" | "outlined" | "borderless";
type ButtonSize = "xSmall" | "small" | "large" | "extraLarge";

export interface IButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  color?: string;
  size?: ButtonSize;
  disabled?: boolean;
  disabledRipple?: boolean;
  fullWidth?: boolean;
  afterIcon?: React.ReactNode;
  loading?: boolean;
  disableGamepadAction?: boolean;
  "data-gamepad"?: string;
  "data-button-sound"?: "back" | "selection";
  hasHoverActive?: boolean;
  loadingText?: string;
  loadingIconSize?: number;
}

export const Button = forwardRef<HTMLButtonElement, IButtonProps>(
  (
    {
      className,
      variant = "contained",
      size = "large",
      disabled = false,
      fullWidth = false,
      color,
      disabledRipple,
      children,
      afterIcon,
      loading,
      disableGamepadAction,
      hasHoverActive,
      loadingText,
      loadingIconSize = 20,
      ...otherProps
    },
    ref,
  ) => {
    const classes = useStyles();
    const currentButtonRef = useRef<HTMLButtonElement>(null);
    const mainButtonRef =
      (ref as RefObject<HTMLButtonElement>) || currentButtonRef;
    function createRipple(event: React.TouchEvent<HTMLButtonElement>) {
      const button = event.currentTarget;
      const circle = document.createElement("span");
      const diameter = Math.max(button.clientWidth, button.clientHeight);
      circle.style.width = circle.style.height = `${diameter}px`;
      circle.classList.add("ripple");

      const ripple = button.getElementsByClassName("ripple")[0];

      if (ripple) {
        ripple.remove();
      }

      button.appendChild(circle);
    }

    function onClickBtn(event: React.MouseEvent<HTMLButtonElement>) {
      if (disabled) return;
      otherProps?.onClick?.(event);
    }

    return (
      <button
        ref={mainButtonRef}
        data-gamepad={disableGamepadAction ? undefined : "button-default"}
        data-selected-class={disableGamepadAction ? undefined : classes.active}
        {...otherProps}
        className={clsx(
          classes.root,
          variant,
          size,
          { disabled },
          { fullWidth },
          afterIcon && !children && "only-icon",
          className,
          hasHoverActive && classes.hoverActive,
        )}
        style={{
          background: color,
        }}
        onTouchStart={(e: React.TouchEvent<HTMLButtonElement>) => {
          if (disabled || disabledRipple) return;
          createRipple(e);
        }}
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => onClickBtn(e)}
      >
        {color === "primary" && !disabled && (
          <div className={classes.overlay} />
        )}
        {loading ? (
          <CircularProgress
            enableAnimation
            size={loadingIconSize}
            borderLight
          />
        ) : loadingText ? (
          <>
            {loadingText}
            <CircularProgress
              enableAnimation
              size={loadingIconSize}
              borderLight
            />
          </>
        ) : (
          <>
            {children}
            {!!afterIcon && afterIcon}
          </>
        )}
      </button>
    );
  },
);

Button.displayName = "Button";

export default Button;
